import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Quote, queryFilter } from '@/modules/Quote/interfaces/quote'
import { showErrors } from '@/utils/snack'

import QuoteDialog from './quote-dialog'
import { translateQuoteStatusKey } from '@/utils/translate'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import { getQuotes, getAllGlobalSearchQuotes } from '@/modules/Quote/gql/quote.gql'
import { quoteStatusClass, quoteType, quoteTypeValues, quoteTypeKeys, quoteStatusClassValues, quoteStatusKeys } from '@/modules/Quote/helpers/quote'
import QuoteForm from '@/modules/Quote/components/QuoteForm.vue'
import RecordTypesHandler from '@/mixins/record-types'
import ListPage from '@/mixins/list-page'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import { PageState } from '../../../store/modules/page'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppTextField,
    AppRefresh,
    AppListSearchBtn,
    AppSelector,
    QuoteForm,
    AppFilterButton,
    AppSimpleTextField,
    AppSimpleSelector
  }
})
export default class QuotesList extends mixins(QuoteDialog, RecordTypesHandler, ListPage) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Cases'
    },
    {
      text: 'Quotes'
    }
  ]
  searchVal = ''
  quotes: Quote[] = []
  ifRelatedList = false

  orderBy: {
    [key: string]: string
  } = {
    name: 'NAME',
    status: 'STATUS',
    id: 'ID'
  }

  translateQuoteStatusKey = (key: string) => translateQuoteStatusKey(key)
  quoteStatusClass = quoteStatusClass
  quoteType = quoteType
  quoteTypeOptions = quoteTypeValues
  quoteStatusOptions = quoteStatusClassValues

  tableColumn = [
    { field: 'name',
      title: 'Quote Name',
      slots: { default: 'name_default' },
      sortable: true,
      resizable: true
    },
    { field: 'accountName',
      title: 'Account Name',
      slots: { default: 'account_name_default' },
      resizable: true
    },
    { field: 'contactName',
      title: 'Contact Name',
      slots: { default: 'contact_name_default' },
      resizable: true
    },
    {
      field: 'status',
      title: 'Quote Status',
      slots: { default: 'status_default' },
      sortable: true,
      width: 150
    },
    { field: 'type',
      title: 'Quote Type',
      slots: { default: 'type_default' },
      width: 150,
      resizable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 74 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'id',
    sortDesc: true
  }

  tableLoading = false

  get recordTypeOptions () {
    if (this.$R.isEmpty(this.recordTypesOptions)) {
      return []
    }
    return this.recordTypesOptions['POLICY'].map(template => template.title)
  }

  @State(state => state.page) page!: PageState

  get isShowFilter () {
    return this.$route.name !== 'QuotesListWithSearchval'
  }

  async created () {
    const { search } = this.$route.params
    this.searchVal = search
    this.restorePage(this.page[this.$route.name || ''])
    await this.getRecordTypes('POLICY')
  }

  async mounted () {
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  getList () {
    if (this.$route.name === 'QuotesListWithSearchval') {
      this.getQuotesFromDashboard()
    } else {
      this.getQuotes()
    }
  }

  async getQuotes () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const {
      quoteName, accountName, quoteStatus, quoteType,
      recordType, caseId, contactId, accountId, carrierProductId,
      producerId, isCaseRelated
    } = this.filter
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getQuotes,
        variables: {
          filter: {
            name: quoteName || null,
            accountName: accountName || null,
            status: quoteStatus + ''
              ? quoteStatusKeys[quoteStatus]
              : null,
            type: quoteType + ''
              ? quoteTypeKeys[quoteType]
              : null,
            recordTypeId: this.recordTypes[recordType]
              ? +this.recordTypes[recordType].id
              : null,
            caseId: caseId ? +caseId : null,
            contactId: contactId ? +contactId : null,
            accountId: accountId ? +accountId : null,
            carrierProductId: carrierProductId ? +carrierProductId : null,
            producerId: producerId ? +producerId : null,
            isCaseRelated
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.tableLoading = false
      this.quotes = result.data.getQuotes.quotes
      this.options.tablePage.total = result.data.getQuotes.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getQuotesFromDashboard () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAllGlobalSearchQuotes,
        variables: {
          query: this.$route.params.search,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.tableLoading = false
      this.quotes = result.data.globalSearchQuotes.quotes
      this.options.tablePage.total = result.data.globalSearchQuotes.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  stopPreventDefault (e: Event) {
    e.preventDefault()
    e.stopPropagation()
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  async deleteQuoteConfirm () {
    await this.deleteQuotePost()
    this.getList()
  }

  async changeProducerConfirm () {
    await this.changeProducerPost()
    this.getList()
  }

  onCreateSuccess () {
    this.hideQuoteDialog()
    this.getList()
  }

  routeToDetail (item: any) {
    return {
      name: 'QuoteDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToAccountDetail (item: any) {
    if (item.account) {
      return {
        name: 'AccountDetailDetail',
        params: {
          id: item.account.id
        }
      }
    }
  }

  routeToContactDetail (item: any) {
    if (item.primaryContact) {
      return {
        name: 'ContactDetailDetail',
        params: {
          id: item.primaryContact.id
        }
      }
    }
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getList()
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getList()
  }
}
